import styled from '@emotion/styled';
import { border_radius_styles_with_size, semantic_colors, semantic_spacing, text_styles } from '../../foundation';
import { buildCSSWithLength, getAutoColorCode } from '../../styles';
import { COLUMN_MIN_HEIGHT } from './constants';
import { CellSize, ColumnAlign } from './types';

const TABLE_HEADER_Z_INDEX = 2;
const TABLE_FIXED_HEADER_Z_INDEX = 3;
const TABLE_FLOATING_HEADER_Z_INDEX = 4;

/** FloatingHeader 는 더 이상 자체 border 를 그리지 않아 border 폭만큼 보정할 필요가 없습니다. */
const getFloatingHeaderOuterWidth = (containerWidth: number) => (containerWidth > 0 ? containerWidth : 0);

const getFloatingHeaderOuterHeight = (headerHeight: number) => (headerHeight > 0 ? headerHeight : 0);

export const StyledTableCol = styled.th<{
  fixed?: boolean;
  fixedColWidth?: number;
  stickyHeader?: boolean;
  stickyOffset?: number;
  groupColumn?: boolean;
  sortable?: boolean;
  resizable?: boolean;
  align?: ColumnAlign;
  valign?: 'middle' | 'top' | 'bottom' | 'baseline';
}>`
  background-color: ${semantic_colors.fill.secondary};
  padding: ${semantic_spacing.table.padding_small.desktop} ${semantic_spacing.table.padding_medium.desktop};
  ${buildCSSWithLength('height', COLUMN_MIN_HEIGHT)};
  z-index: ${TABLE_HEADER_Z_INDEX};
  vertical-align: ${({ valign }) => valign};
  ${text_styles.Body_12_SemiBold};
  color: ${semantic_colors.content.secondary};
  text-align: ${({ align }) => align ?? 'left'};
  border: none;
  transition: background-color 0.1s linear;
  ${({ stickyHeader, fixed }) => (stickyHeader || fixed) && `position: sticky;`};
  ${({ stickyHeader, fixed, groupColumn, stickyOffset = 0 }) =>
    (stickyHeader || fixed) && `${buildCSSWithLength('top', stickyOffset + (groupColumn ? COLUMN_MIN_HEIGHT : 0))};`};
  ${({ fixed, fixedColWidth }) =>
    fixed && `z-index: ${TABLE_FIXED_HEADER_Z_INDEX}; ${buildCSSWithLength('left', fixedColWidth ?? 0)};`};
  ${({ sortable }) => {
    const { hover, pressed } = getAutoColorCode(semantic_colors.fill.secondary);
    return (
      sortable &&
      `
      cursor: pointer;
      &:hover {
        background-color: ${hover};
      }
      &:active {
        background-color: ${pressed};
      }
    `
    );
  }}
  ${({ resizable }) =>
    resizable &&
    `resize: horizontal; overflow: hidden;
    .ContentsWrapper {
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }`}
`;

export const cell_size_css: Record<CellSize, string> = {
  small: `
    padding: ${semantic_spacing.table.padding_small.desktop};
  `,
  medium: `
    padding: ${semantic_spacing.table.padding_small.desktop} ${semantic_spacing.table.padding_medium.desktop};
  `,
  large: `
    padding: ${semantic_spacing.table.padding_large.desktop};
  `,
};

export const StickyHeaderHost = styled.div`
  position: relative;
  overflow: visible;
`;

export const FloatingHeader = styled.div<{
  $visible: boolean;
  $stickyOffset: number;
  $containerWidth: number;
  $headerHeight: number;
}>`
  position: sticky;
  ${({ $stickyOffset }) => buildCSSWithLength('top', $stickyOffset)};
  ${({ $containerWidth }) => buildCSSWithLength('width', getFloatingHeaderOuterWidth($containerWidth))};
  ${({ $headerHeight }) => buildCSSWithLength('height', getFloatingHeaderOuterHeight($headerHeight))};
  margin-bottom: ${({ $headerHeight }) => {
    const outerHeight = getFloatingHeaderOuterHeight($headerHeight);

    return outerHeight > 0 ? `-${outerHeight}px` : 0;
  }};
  box-sizing: border-box;
  background-color: ${semantic_colors.fill.secondary};
  border-radius: ${border_radius_styles_with_size.small}px;
  overflow: hidden;
  z-index: ${TABLE_FLOATING_HEADER_Z_INDEX};
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};

  .pds-table-cell-fixed {
    transform: translateX(var(--pds-table-sticky-scroll-left, 0px));
  }
`;

export const TranslateInner = styled.div<{ $tableWidth: number }>`
  ${({ $tableWidth }) => buildCSSWithLength('width', $tableWidth)};
  will-change: transform;
`;

export const StickyHeaderSentinel = styled.div`
  height: 1px;
  margin-bottom: -1px;
  pointer-events: none;
`;
